// Google Tag Manager container loader.
//
// WHY THIS IS NOT IN THE <head>
// The standard GTM snippet loads the container on every page view. That is safe
// only when every tag inside the container is itself consent-gated. This
// container (GTM-TX7DH28Q) is not: it carries Meta, TikTok and Snapchat pixels
// that fire on container load. Google Consent Mode does NOT gate third-party
// tags — `consent default denied` only suppresses Google's own tags — so those
// pixels would run before the user has answered the cookie banner.
//
// Instead the container is loaded once the user has granted `marketing`
// consent (see CookieConsentUtils.setCookiesBasedOnPreferences). Statistic
// alone is not enough: every tag in the container is advertising.
// The Consent Mode defaults and the gtag shim still ship in the document head
// (src/app/layout.tsx) so consent signals are queued on dataLayer before the
// container arrives and are replayed to it in order.
//
// ponytail: hardcoded container ID — it is a public value and the same across
// environments. Move to a runtime env var only if int/prod need separate
// containers.
const GTM_ID = "GTM-TX7DH28Q";
const GTM_SRC = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

let _loaded = false;

/**
 * Watch <body> for, and strip, stray text nodes left by GTM Custom HTML tags.
 *
 * A Custom HTML tag is injected as HTML. Several tags in GTM-TX7DH28Q have
 * their pixel calls pasted WITHOUT a <script> wrapper (tag ids 11, 13, 16, 17,
 * 18 at time of writing), so GTM appends the JS source as a bare text node to
 * <body>. Only the UA stylesheet's `script { display: none }` hides real
 * script bodies — a text node is page content, so it paints. The result is
 * lines like `fbq('track', 'InitiateCheckout');` printed under the footer.
 *
 * The node is a sibling of the React root, so React never reconciles it away
 * and it survives every client-side navigation — which is why it turns up on
 * pages that did not fire the tag (e.g. /cancel after /checkout).
 *
 * This only hides the symptom: those tags still never execute, so the Meta and
 * TikTok events stay unsent. The real fix is wrapping the tag bodies in
 * <script> in the GTM container.
 *
 * A non-empty text node directly under <body> is never legitimate here — Next
 * renders into an element root and only whitespace sits between siblings. The
 * whitespace matters: a well-formed tag arrives as text, <script>, text, and
 * the trim check is what keeps those gaps intact.
 *
 * Rests on GTM appending Custom HTML straight to <body> rather than into a
 * wrapper — confirmed against the live container, but undocumented on their
 * side. If GTM ever nests the injection this silently stops working, so check
 * that first on the next sighting.
 *
 * ponytail: observes <body> childList only (not subtree) and stays connected
 * for the session because click-triggered tags inject long after load. One
 * observer per successful init; a duplicate would be harmless anyway, since
 * parentNode?. short-circuits on an already-detached node.
 */
function watchForStrayTagText(): void {
  const isStray = (node: Node): boolean =>
    node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim() !== "";

  const sweep = (nodes: ArrayLike<Node>): void => {
    Array.from(nodes)
      .filter(isStray)
      .forEach((node) => node.parentNode?.removeChild(node));
  };

  sweep(document.body.childNodes);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => sweep(mutation.addedNodes));
  }).observe(document.body, { childList: true });
}

/**
 * Inject the GTM container. Idempotent, browser-only.
 * MUST only be called after the user has granted marketing consent
 * (callers enforce this).
 */
export function initGtm(): void {
  if (_loaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];

    // Isolated: this guard is cosmetic, so nothing it throws may stop the
    // container from loading. Every tag and conversion rides on that append.
    try {
      watchForStrayTagText();
    } catch (error) {
      console.error("GTM stray-text guard failed:", error);
    }

    if (document.querySelector(`script[src="${GTM_SRC}"]`)) {
      _loaded = true;
      return;
    }

    // GTM measures container start time from this event.
    w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    const script = document.createElement("script");
    script.async = true;
    script.src = GTM_SRC;
    document.head.appendChild(script);

    _loaded = true;
  } catch (error) {
    console.error("GTM init failed:", error);
  }
}
